import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js'
import moment from 'moment'
import {
  CreateHomePageSection,
  SingleSection,
} from '../redux-toolkits/home-page/homePage.type'
import { ErrorType } from '../redux-toolkits/shared/shared.type'
import { Vendor } from '../redux-toolkits/vendors/vendors.types'
import { FulfilmentCentre } from '../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { EventTicketTier } from '../redux-toolkits/events/events.type'
import {
  Combination,
  ComboItemParentRaw,
  CreatedProductVariantOption,
  Product,
  ProductType,
  TransformedVariant,
} from '../redux-toolkits/products/products.type'
export const formatCurrency = (
  amount: number | string,
  {
    decimalPlaces = 2,
    currency = 'NGN',
  }: {
    decimalPlaces?: number
    currency?: string
  } = {
    decimalPlaces: 2,
    currency: 'NGN',
  },
) => {
  return formatNumber(amount, {
    decimalPlaces: decimalPlaces,
    style: currency ? 'currency' : 'decimal',
    currency: currency,
  })
}

export const formatNumber = (
  amount: number | string,
  {
    decimalPlaces = 2,
    style = 'decimal',
    currency,
  }: {
    decimalPlaces?: number
    style?: keyof Intl.NumberFormatOptionsStyleRegistry
    currency?: string
  } = {},
) => {
  if (amount === Infinity) {
    return '∞'
  }

  let amountStr = ''

  amountStr = `${String(amount).split('.')[0]}.${
    String(amount).split('.')[1] ?? '00'
  }`

  amountStr = amountStr.replace(/[^\d.-]/g, '')

  const numericAmount = parseFloat(amountStr)

  if (isNaN(numericAmount)) {
    return ''
  }

  let result = ''

  const formatter = new Intl.NumberFormat('en-NG', {
    style: style,
    currency: currency,
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  })

  result = formatter.format(
    style === 'percent' ? numericAmount / 100 : numericAmount,
  )

  return result.replace('.00', '')
}

export const parseCurrencyToNumber = (
  nairaString?: string | number,
): number => {
  try {
    const numericString =
      String(nairaString).replace(/[^\d.-]/g, '') !== ''
        ? String(nairaString).replace(/[^\d.-]/g, '')
        : '0'

    const numericValue = parseFloat(numericString)

    if (isNaN(numericValue)) {
      throw new Error('Invalid naira format')
    }

    return numericValue
  } catch (error) {
    return 0
  }
}

export const formatPercentage = (amount: number | string) => {
  if (amount === Infinity) {
    return '∞'
  }

  const numericAmount = Number(String(amount).replace(/[^\d.-]/g, '') ?? 0)

  if (isNaN(numericAmount)) {
    return 'Invalid amount' // Return a default message or handle the error as needed
  }

  return numericAmount.toLocaleString() + '%'
}

export const getErrorMessage = (error: ErrorType) => {
  return error.error?.data?.error ?? 'Something went wrong, try again later.'
}

export const removeElementAtIndex = (array: any[], index: number) => {
  // Check if the index is within bounds
  if (index >= 0 && index < array.length) {
    array.splice(index, 1)
  }
  return array
}

export const prettyFormatNumber = (num: number) => {
  if (num === null || num === undefined) return ''

  const absNum = Math.abs(num)
  if (absNum < 1000) return `${num}`

  const units = ['K', 'M', 'B', 'T']
  const index = Math.floor(Math.log10(absNum) / 3) // Find the appropriate index
  const unit = units[index - 1] || '' // Get the unit suffix

  // Format the number and append the unit
  return `${(num / Math.pow(1000, index)).toFixed(1)}${unit}`
}

export const getVendorName = (vendor?: Vendor) => {
  return !vendor
    ? undefined
    : vendor?.businessInformation?.businessName ??
        vendor?.name ??
        (vendor?.firstName && vendor?.lastName
          ? `${vendor?.firstName} ${vendor?.lastName}`
          : undefined) ??
        vendor?.email ??
        'N/A'
}

export const getTimeAgo = (timeString: string): string => {
  try {
    const time = new Date(timeString).getTime()
    const now = Date.now()
    const secondsAgo = Math.floor((now - time) / 1000)

    if (secondsAgo < 60) {
      return `${secondsAgo} ${secondsAgo === 1 ? 'second' : 'seconds'} ago`
    }

    const minutesAgo = Math.floor(secondsAgo / 60)
    if (minutesAgo < 60) {
      return `${minutesAgo} ${minutesAgo === 1 ? 'minute' : 'minutes'} ago`
    }

    const hoursAgo = Math.floor(minutesAgo / 60)
    if (hoursAgo < 24) {
      return `${hoursAgo} ${hoursAgo === 1 ? 'hour' : 'hours'} ago`
    }

    const daysAgo = Math.floor(hoursAgo / 24)
    return `${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`
  } catch (error) {
    return 'N/A'
  }
}

export const convertSize = (size: number) => {
  if (size > 1048576) {
    return `${Math.ceil(size / 1048576)} MB`
  }
  return `${Math.ceil(size / 1024)} KB`
}

export const calculateProductPrice = (product?: Product) => {
  if (!product) {
    return formatCurrency(0)
  }

  return formatCurrency(
    calculateProductPriceRaw(
      product.price,
      product?.comboItems?.map(({ _id, ...others }) => {
        return { ...others }
      }) ?? [],
      product.isMarkupEnabled ?? false,
      product.markupValue ?? 0,
    ),
  )
}

export const calculateProductPriceRaw = (
  price: number,
  comboItems: ComboItemParentRaw[],
  isMarkupEnabled: boolean,
  markupValue: number,
) => {
  let totalComboPrice = 0

  totalComboPrice = comboItems.reduce((acc, item) => {
    return (
      acc +
      item?.items?.reduce((subacc, subitem) => {
        return subacc + subitem.price * subitem.minimumQuantity
      }, 0)
    )
  }, 0)

  let productPrice = price

  if (isMarkupEnabled && markupValue) {
    productPrice = productPrice + getMarkUpValue(markupValue, productPrice)
  }

  return totalComboPrice + productPrice
}

export const calculateVariantCombinationPriceRaw = (
  variantCombination: Combination[],
  variants: CreatedProductVariantOption[],
) => {
  return sumArray(
    variantCombination.map((vC) => {
      const v = variants.find(
        (el) =>
          (typeof el.variant === 'string'
            ? (el.variant as unknown as string)
            : el.variant?._id) === vC.variant?._id && el.value === vC.value,
      )

      return (v?.price ?? 0) + ((v?.markupValue ?? 0) / 100) * (v?.price ?? 0)
    }),
  )
}

export const reorderProducts = (
  list: Product[],
  startIndex: number,
  endIndex: number,
) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

export const getMarkUpValue = (percent: number, amount: number) => {
  return Number((percent / 100) * amount)
}

export const formatTime = (
  time: string | Date | moment.Moment | undefined | null,
  format: string = 'hh:mm A',
): string => {
  try {
    if (!time) {
      return ''
    }
    const momentTime = moment(time)
    if (!momentTime.isValid()) {
      throw new Error('Invalid time')
    }
    return momentTime.format(format)
  } catch (error) {
    return 'Invalid time'
  }
}

export const parseTimeToDate = (
  time: string,
  format: string = 'h:mm A',
): Date | null => {
  try {
    const momentTime = moment(time.trim(), [format, 'hh:mm A'], true)
    if (!momentTime.isValid()) {
      throw new Error('Invalid time format')
    }
    return momentTime.toDate()
  } catch (error) {
    console.log(time, error)
    return null
  }
}

export const parseDate = (
  date: undefined | null | string | moment.Moment,
): moment.Moment | null => {
  if (date === null) {
    return null
  } else if (moment.isMoment(date) && date.isValid()) {
    return date
  } else {
    try {
      const response = moment(date)
      if (response.isValid()) {
        return response
      } else {
        return null
      }
    } catch {
      return null
    }
  }
}

export const getPageFromSkip = (
  skip: number | string,
  limit: number | string,
) => {
  return Math.floor(Number(skip) / Number(limit)) + 1
}

export const getSkipFromPage = (
  page: number | string,
  limit: number | string,
) => {
  return (Number(page) - 1) * Number(limit)
}

export const deepCopy = <T extends object>(item: T): T => {
  return JSON.parse(JSON.stringify(item))
}

export const moveElement = <T>(
  array: T[],
  startIndex: number,
  endIndex: number,
): T[] => {
  const arrayCopy = JSON.parse(JSON.stringify(array))
  const [element] = arrayCopy.splice(startIndex, 1)
  arrayCopy.splice(endIndex, 0, element)
  return arrayCopy
}

export const isNumeric = (str: string): boolean =>
  typeof str === 'string' && str.trim() !== '' && !isNaN(Number(str))

export const sumArray = (numbers: number[]): number =>
  numbers.reduce((total, num) => total + num, 0)

export const transformVariants = (variants: CreatedProductVariantOption[]) => {
  let transformed: TransformedVariant[] = []

  if (variants.length > 0) {
    transformed = variants
      .reduce((acc: TransformedVariant[], curr) => {
        // Find if the variant already exists in the accumulator
        const existing = acc.find(
          (item) => item.variant?._id === curr.variant?._id,
        )

        if (existing) {
          // If found, push the values into the value array
          existing.values.push(curr.value)
        } else {
          // If not found, create a new object for that variant with the values as an array
          acc.push({
            variant: curr.variant,
            values: [curr.value],
          })
        }
        return acc
      }, [])
      .sort((a, b) => a.variant.name.localeCompare(b.variant.name))
  } else {
    transformed = []
  }

  return transformed
}

export const areArraysSame = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false

  const normalize = (arr: string[]) =>
    arr.map((str) => str.toLowerCase()).sort()

  const normA = normalize(a)
  const normB = normalize(b)

  return normA.every((val, index) => val === normB[index])
}

export const stripSingleSectionFields = (
  section: SingleSection,
): CreateHomePageSection => {
  const others = omit(section, [
    '_id',
    '__v',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
    'order',
    'slug',
  ])

  return {
    ...others,
    filters: others.filters ? omit(others.filters, ['_id']) : undefined,
    items: others.items.map((i) => {
      return {
        type: i.type,
        item: i?.item?._id,
        homepageBanner:
          i.type === 'HOMEPAGE_BANNER' && i.homepageBanner
            ? i.homepageBanner
            : undefined,
        manualContent:
          i.type === 'MANUAL_ITEM' && i.manualContent
            ? omit(i.manualContent, ['_id'])
            : undefined,
      }
    }),
  }
}

export const omit = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[],
): Omit<T, K> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key as K)),
  ) as Omit<T, K>
}

export const retain = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[],
): Omit<T, K> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => keys.includes(key as K)),
  ) as Omit<T, K>
}

export const getProductType = (product: Product): ProductType => {
  if (product.isCombo && product.isVariant) {
    return 'variantCombo'
  } else if (product.isCombo && !product.isVariant) {
    return 'combo'
  } else if (!product.isCombo && product.isVariant) {
    return 'variant'
  } else if (
    !product.isCombo &&
    !product.isVariant &&
    ((product.bundledProducts?.length ?? 0 > 0) ||
      product.productType === 'BUNDLE')
  ) {
    return 'bundle'
  } else {
    return 'single'
  }
}

export const getCurrencySymbol = (
  currency: string,
  locale: string = 'en-NG',
): string => {
  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).formatToParts(0)

  const symbol = parts.find((p) => p.type === 'currency')?.value

  if (!symbol) {
    throw new Error(`Invalid currency code: ${currency}`)
  }

  return symbol
}

export const parsePhoneNumber = (number: string) => {
  const cleanedNumber = number.replaceAll(' ', '')

  const isValid = /^\+?\d+$/.test(cleanedNumber)

  if (!isValid) {
    return undefined
  }

  return parsePhoneNumberFromString(cleanedNumber)
}

export const isPhoneNumberValid = (
  number: string,
  countryCode: CountryCode,
) => {
  const cleanedNumber = number.replaceAll(' ', '')

  const isValid = /^\+?\d+$/.test(cleanedNumber)

  if (!isValid) {
    return false
  }

  const phonePrefixes = ['70', '71', '80', '81', '90', '91']

  const parsedNumber = parsePhoneNumberFromString(cleanedNumber, countryCode)

  return countryCode === 'NG'
    ? parsedNumber?.isValid() &&
        parsedNumber?.nationalNumber?.length === 10 &&
        phonePrefixes.includes(parsedNumber?.nationalNumber?.slice(0, 2))
    : parsedNumber?.isValid() ?? false
}

export const formatDate = (
  date: string | Date | moment.Moment | undefined | null,
  format: string = 'MMMM DD, YYYY',
  config: {
    mode?: 'fromUtc' | 'toUtc'
  } = {
    mode: undefined,
  },
): string => {
  // Formats a date value representation into the specified string format

  try {
    if (!date) {
      return ''
    }
    const dateVal = date

    const momentDate = moment(dateVal)

    if (!momentDate.isValid()) {
      throw new Error('Invalid date')
    }

    let formattedDate: string

    if (config.mode === 'fromUtc') {
      formattedDate = momentDate.utc().local().format(format)
    } else if (config.mode === 'toUtc') {
      formattedDate = momentDate.utc().format(format)
    } else {
      formattedDate = momentDate.format(format)
    }

    return formattedDate
  } catch (error) {
    return 'Invalid date'
  }
}

export const parseDateStringToDate = (
  dateString?: string,
  format?: string,
  strict?: boolean,
  config: {
    mode?: 'fromUtc'
  } = {
    mode: undefined,
  },
): Date | null => {
  try {
    if (!dateString) {
      return null
    }

    let momentDate: moment.Moment

    if (config.mode === 'fromUtc') {
      momentDate = moment.utc(dateString, format, strict).local()
    } else {
      momentDate = moment(dateString, format, strict)
    }

    if (!momentDate.isValid()) {
      throw new Error('Invalid date format')
    }

    return momentDate.toDate()
  } catch (error) {
    return null
  }
}

export const getFilenameFromDisposition = (
  response: Response,
  fallback = 'download.xlsx',
) => {
  const disposition = response.headers.get('content-disposition')

  if (!disposition) return fallback

  // RFC 5987 / filename*
  const filenameStarMatch = disposition.match(
    /filename\*\s*=\s*UTF-8''([^;]+)/i,
  )
  if (filenameStarMatch) {
    return decodeURIComponent(filenameStarMatch[1])
  }

  // Regular filename
  const filenameMatch = disposition.match(/filename\s*=\s*"?(.*?)"?(;|$)/i)
  if (filenameMatch) {
    return filenameMatch[1]
  }

  return fallback
}

export const formatDateTime = (
  date: string,
  time: string,
  config: {
    dateFormat?: string
    timeFormat?: string
    resultFormat?: string
  },
) => {
  // Get time values
  const parsedTime = parseTimeToDate(time, config.timeFormat)!

  // Get date from string, this converts to local time...from utc which the date string comes in
  const mergedDate = parseDateStringToDate(
    date,
    config.dateFormat,
    !!config.dateFormat,
  )!

  // Include time values to date
  mergedDate.setHours(
    parsedTime.getHours(),
    parsedTime.getMinutes(),
    parsedTime?.getSeconds(),
    parsedTime?.getMilliseconds(),
  )

  return formatDate(mergedDate, config.resultFormat)
}

export const getFulfilmentCenterText = (centers: FulfilmentCentre[]) => {
  if ((centers?.length ?? 0) === 0) {
    return 'No selection'
  } else {
    return centers.length === 1
      ? centers[0].name
      : `${centers[0].name} & ${centers.length - 1} others`
  }
}

export function cleanObject(obj: any) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== '' && value !== undefined,
    ),
  )
}

export const getEventPriceText = (
  price: number,
  tiers: EventTicketTier[],
): string => {
  if (tiers.length > 0) {
    const minPrice = Math.min(...tiers.map((t) => t.price))
    return `from ${formatCurrency(minPrice)}`
  }
  if (price === 0) return 'FREE'
  return formatCurrency(price)
}
